import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Loader2, Sparkles, CheckCircle } from 'lucide-react';
import { generateForm, type GeneratedForm } from '@/services/aiFormService';

interface AIFormGeneratorProps {
  onFormGenerated?: (form: GeneratedForm) => void;
  initialPrompt?: string;
}

const AIFormGenerator: React.FC<AIFormGeneratorProps> = ({ onFormGenerated, initialPrompt }) => {
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<GeneratedForm | null>(null);

  // Auto-generate if initialPrompt is provided
  useEffect(() => {
    if (initialPrompt && !loading && !form) {
      handleGenerate();
    }
  }, [initialPrompt]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);
    setForm(null);

    try {
      const result = await generateForm(prompt);
      setForm(result);
      if (onFormGenerated) {
        onFormGenerated(result);
      }
    } catch (err: any) {
      console.error('AI Generation Error:', err);
      if (err.message?.includes('AI_QUOTA_EXCEEDED')) {
        setError("QUOTA_REACHED");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading && prompt.trim()) {
        handleGenerate();
      }
    }
  };

  const SUGGESTED_PROMPTS = [
    "A job application form with resume upload",
    "Customer satisfaction feedback survey",
    "Event registration with dietary requirements"
  ];

  return (
    <div className="space-y-6 w-full max-w-2xl mx-auto">
      <Card className="border-primary/20 bg-card/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Form Generator
          </CardTitle>
          <CardDescription>
            Describe the form you want to build and let AI handle the rest.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prompt">What should this form do?</Label>
            <Textarea
              id="prompt"
              placeholder="e.g., A contact form for a real estate agency with name, email, and property interest..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="min-h-[100px] resize-none focus-visible:ring-primary/30"
            />
            {/* Suggested Prompts */}
            {!loading && (
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTED_PROMPTS.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => setPrompt(suggestion)}
                    className="text-xs bg-muted hover:bg-muted/80 text-muted-foreground px-3 py-1.5 rounded-full transition-colors border border-border/50 text-left"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className={`p-4 rounded-lg flex flex-col gap-3 ${
              error === "QUOTA_REACHED" 
                ? "bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-500" 
                : "bg-destructive/10 border border-destructive/20 text-destructive"
            }`}>
              <div className="flex items-center gap-2 text-sm font-medium">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error === "QUOTA_REACHED" 
                  ? "Daily AI Generation Limit Reached" 
                  : error}
              </div>
              
              {error === "QUOTA_REACHED" && (
                <div className="space-y-3">
                  <p className="text-xs opacity-90 leading-relaxed">
                    You've reached your free limit of 5 AI generations per day. 
                    Upgrade to <strong>Pro</strong> or <strong>Starter</strong> to enjoy higher limits and build unlimited forms with AI!
                  </p>
                  <Button asChild size="sm" variant="default" className="w-full bg-amber-600 hover:bg-amber-700 text-white border-none h-8">
                    <Link to="/billing">Upgrade Now</Link>
                  </Button>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            className="w-full h-11 bg-primary hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Form
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {form && (
        <Card className="border-green-500/20 bg-card/50 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4">
          <CardHeader className="border-b border-border/10 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-bold">{form.title}</CardTitle>
                <CardDescription className="mt-1">{form.description}</CardDescription>
              </div>
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="space-y-4">
              {form.fields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium">
                      {field.label}
                      {field.required && <span className="text-destructive ml-0.5">*</span>}
                    </Label>
                  </div>

                  {field.type === 'textarea' ? (
                    <Textarea
                      placeholder={field.placeholder}
                      disabled
                      className="bg-muted/30 border-border/50 opacity-100 placeholder:text-muted-foreground/50"
                    />
                  ) : field.type === 'select' ? (
                    <Select disabled>
                      <SelectTrigger className="bg-muted/30 border-border/50 opacity-100 text-muted-foreground/50">
                        <SelectValue placeholder={field.placeholder || "Select an option"} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map((opt, i) => (
                          <SelectItem key={i} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
                      placeholder={field.placeholder}
                      disabled
                      className="bg-muted/30 border-border/50 opacity-100 placeholder:text-muted-foreground/50"
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIFormGenerator;
